import { supabase } from '../lib/supabase';
import { getFromStorage, setInStorage, generateId, STORAGE_KEYS } from './storage';
import { getCurrentTenantId } from './tenantScope';

const toCustomer = (row) =>
  row
    ? {
        id: row.id,
        employeeNo: row.employee_no ?? row.employeeNo ?? '',
        name: row.name || '',
        department: row.department || '',
        phone: row.phone || '',
        employeeType: row.employee_type || 'regular',
        tenantId: row.tenant_id || null,
        createdAt: row.created_at,
      }
    : null;

function isMissingColumnError(error, column) {
  const msg = error?.message || '';
  return (
    new RegExp(`\\b${column}\\b`, 'i').test(msg) &&
    /does not exist|schema cache|could not find/i.test(msg)
  );
}

export async function getCustomers() {
  const tenantId = getCurrentTenantId();
  if (supabase) {
    let query = supabase.from('customers').select('*').order('name');
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (!error && data) {
      const list = data.map(toCustomer);
      setInStorage(STORAGE_KEYS.CUSTOMERS, list);
      return list;
    }
    if (tenantId && error) {
      const { data: allData, error: allError } = await supabase
        .from('customers')
        .select('*')
        .order('name');
      if (!allError && allData) {
        const list = allData.map(toCustomer);
        setInStorage(STORAGE_KEYS.CUSTOMERS, list);
        return list;
      }
    }
  }
  const all = getFromStorage(STORAGE_KEYS.CUSTOMERS, []);
  return tenantId ? all.filter((c) => !c.tenantId || c.tenantId === tenantId) : all;
}

export async function addCustomer(data) {
  const tenantId = getCurrentTenantId();
  const payload = {
    employee_no: data.employeeNo?.trim() || '',
    name: data.name?.trim() || '',
    department: data.department?.trim() || '',
    phone: data.phone?.trim() || '',
    employee_type: data.employeeType || 'regular',
    tenant_id: tenantId || null,
  };
  let result = null;

  if (supabase) {
    let insertPayload = { ...payload };
    let { data: row, error } = await supabase
      .from('customers')
      .insert(insertPayload)
      .select()
      .single();

    if (error && insertPayload.employee_no !== undefined && isMissingColumnError(error, 'employee_no')) {
      const { employee_no, ...rest } = insertPayload;
      insertPayload = rest;
      ({ data: row, error } = await supabase
        .from('customers')
        .insert(insertPayload)
        .select()
        .single());
    }
    if (error && insertPayload.tenant_id) {
      const { tenant_id, ...legacyPayload } = insertPayload;
      ({ data: row, error } = await supabase
        .from('customers')
        .insert(legacyPayload)
        .select()
        .single());
    }
    if (!error) result = toCustomer(row);
  }

  const customers = getFromStorage(STORAGE_KEYS.CUSTOMERS, []);
  const customer = {
    id: result?.id || generateId(),
    employeeNo: payload.employee_no,
    name: payload.name,
    department: payload.department,
    phone: payload.phone,
    employeeType: payload.employee_type,
    tenantId: tenantId || null,
    createdAt: result?.createdAt || new Date().toISOString(),
  };
  if (result) {
    const idx = customers.findIndex((c) => c.id === result.id);
    if (idx >= 0) customers[idx] = customer;
    else customers.push(customer);
  } else {
    customers.push(customer);
  }
  setInStorage(STORAGE_KEYS.CUSTOMERS, customers);
  return result || customer;
}

export async function updateCustomer(id, data) {
  const updates = {};
  if (data.employeeNo !== undefined) updates.employee_no = data.employeeNo?.trim() ?? '';
  if (data.name !== undefined) updates.name = data.name?.trim() ?? '';
  if (data.department !== undefined) updates.department = data.department?.trim() ?? '';
  if (data.phone !== undefined) updates.phone = data.phone?.trim() ?? '';
  if (data.employeeType !== undefined) updates.employee_type = data.employeeType ?? 'regular';

  if (Object.keys(updates).length === 0) return null;

  if (supabase) {
    let updatePayload = { ...updates };
    let { data: row, error } = await supabase
      .from('customers')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (error && updatePayload.employee_no !== undefined && isMissingColumnError(error, 'employee_no')) {
      const { employee_no, ...rest } = updatePayload;
      updatePayload = rest;
      if (Object.keys(updatePayload).length === 0) {
        // Column missing — keep employeeNo in local cache only
        const customers = getFromStorage(STORAGE_KEYS.CUSTOMERS, []);
        const idx = customers.findIndex((c) => c.id === id);
        if (idx >= 0) {
          customers[idx] = {
            ...customers[idx],
            employeeNo: data.employeeNo?.trim() ?? '',
            ...(data.name !== undefined ? { name: data.name?.trim() ?? '' } : {}),
            ...(data.department !== undefined ? { department: data.department?.trim() ?? '' } : {}),
            ...(data.phone !== undefined ? { phone: data.phone?.trim() ?? '' } : {}),
            ...(data.employeeType !== undefined ? { employeeType: data.employeeType } : {}),
          };
          setInStorage(STORAGE_KEYS.CUSTOMERS, customers);
          return customers[idx];
        }
      } else {
        ({ data: row, error } = await supabase
          .from('customers')
          .update(updatePayload)
          .eq('id', id)
          .select()
          .single());
      }
    }

    if (!error && row) {
      const customers = getFromStorage(STORAGE_KEYS.CUSTOMERS, []);
      const idx = customers.findIndex((c) => c.id === id);
      if (idx >= 0) {
        customers[idx] = toCustomer(row);
        if (data.employeeNo !== undefined && customers[idx].employeeNo === '' && data.employeeNo) {
          customers[idx].employeeNo = data.employeeNo.trim();
        }
        setInStorage(STORAGE_KEYS.CUSTOMERS, customers);
      }
      return toCustomer(row);
    }
  }

  const customers = getFromStorage(STORAGE_KEYS.CUSTOMERS, []);
  const index = customers.findIndex((c) => c.id === id);
  if (index === -1) return null;
  customers[index] = {
    ...customers[index],
    ...(data.employeeNo !== undefined ? { employeeNo: data.employeeNo?.trim() ?? '' } : {}),
    ...(data.name !== undefined ? { name: data.name?.trim() ?? '' } : {}),
    ...(data.department !== undefined ? { department: data.department?.trim() ?? '' } : {}),
    ...(data.phone !== undefined ? { phone: data.phone?.trim() ?? '' } : {}),
    ...(data.employeeType !== undefined ? { employeeType: data.employeeType } : {}),
  };
  setInStorage(STORAGE_KEYS.CUSTOMERS, customers);
  return customers[index];
}

export async function deleteCustomer(id) {
  if (supabase) {
    const { error } = await supabase.from('customers').delete().eq('id', id);
    if (error) throw new Error(error.message || 'Failed to delete customer from database');
  }
  const customers = getFromStorage(STORAGE_KEYS.CUSTOMERS, []).filter((c) => c.id !== id);
  setInStorage(STORAGE_KEYS.CUSTOMERS, customers);
  return true;
}

export async function getCustomerById(id) {
  const customers = await getCustomers();
  return customers.find((c) => c.id === id) || null;
}

/** Display label helpers — include employee no when present. */
export function formatCustomerLabel(customer, { withDepartment = true } = {}) {
  if (!customer) return 'Walk-in';
  const no = (customer.employeeNo || '').trim();
  const name = (customer.name || '').trim() || 'Unknown';
  const dept = (customer.department || '').trim() || 'Other';
  const who = no ? `${no} · ${name}` : name;
  return withDepartment ? `${who} — ${dept}` : who;
}

export function formatCustomerSaleName(customer) {
  if (!customer) return 'Walk-in';
  const no = (customer.employeeNo || '').trim();
  const name = (customer.name || '').trim() || 'Unknown';
  const dept = (customer.department || '').trim() || 'Other';
  const who = no ? `${no} · ${name}` : name;
  return `${who} (${dept})`;
}
